import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { Tooltip, Box } from "@mui/material";
import IncidentTableHead from "./IncidentTableHead";
import dayjs from "dayjs";

const IncidentTable = ({
  loading,
  incidents,
  selectedParentDomain,
  selectedDomain,
  selectedSubDomain,
  selectedProduct,
  searchValue,
  selectedCategory,
  resetAll,
  pagination,
  handlePageChange,
  PAGE_SIZE,
  sortColumn,
  sortOrder,
  handleSort,
  columnSearch,
  onColumnSearchChange,
  setResolutionPendingStatus
}) => {
  // Sort incidents (filtering is now done server-side via API)
  const filteredAndSortedIncidents = useMemo(() => {
    let filtered = [...incidents];

    // Apply sorting
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        switch (sortColumn) {
          case "snowId":
            aValue = (a.snowId || "").toLowerCase();
            bValue = (b.snowId || "").toLowerCase();
            break;
          case "incidentDescription":
            aValue = (a.incidentDescription || "").toLowerCase();
            bValue = (b.incidentDescription || "").toLowerCase();
            break;
          case "incidentCategory":
            aValue = (a.displayIncidentCategory || a.incidentCategory || "").toLowerCase();
            bValue = (b.displayIncidentCategory || b.incidentCategory || "").toLowerCase();
            break;
          case "assignedTo":
            aValue = (a.assignedTo || "").toLowerCase();
            bValue = (b.assignedTo || "").toLowerCase();
            break;
          case "status":
            aValue = (a.status || "").toLowerCase();
            bValue = (b.status || "").toLowerCase();
            break;
          case "resolutionPendingStatus":
            // Extract numeric value for proper sorting
            aValue = parseInt((a.resolutionPendingStatus || "0").replace(/\D/g, "")) || 0;
            bValue = parseInt((b.resolutionPendingStatus || "0").replace(/\D/g, "")) || 0;
            break;
          case "createdAt": // Support both for backward compatibility
            // Sort by date timestamp
            aValue = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
            bValue = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [incidents, sortColumn, sortOrder]);

  const displayIncidents = filteredAndSortedIncidents;
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
         {/* Pagination */}
         {!loading && displayIncidents.length > 0 && (
        <div className="bg-gray-50 px-4  py-4 flex items-center justify-between border-t">
          <div className="text-[16px] text-gray-700">
            Total Incidents:  {pagination?.totalCount || 0}
            
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <div className="flex items-center space-x-1">
              {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                
                return (
                  <button
                    key={i}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 text-sm font-medium rounded-md ${
                      pagination.page === pageNum
                        ? 'bg-[#bb1ccc] text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <IncidentTableHead
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            handleSort={handleSort}
            columnSearch={columnSearch}
            onColumnSearchChange={onColumnSearchChange}
            setResolutionPendingStatus={setResolutionPendingStatus}
          />
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              // Skeleton Loading
              [...Array(5)].map((_, index) => (
                <tr key={index} className="animate-pulse">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </td>
                  <td className="px-6 py-4" style={{ width: '300px', minWidth: '300px', maxWidth: '300px' }}>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mt-2"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-28"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </td>
                </tr>
              ))
            ) : displayIncidents.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-20">
                  <div className="flex flex-col items-center justify-center text-center">
                    <svg
                      className="w-24 h-24 text-gray-300 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      No Incidents Found
                    </h3>
                    <p className="text-gray-500 max-w-md">
                      {selectedParentDomain || selectedDomain || selectedSubDomain || selectedProduct || searchValue || selectedCategory
                        ? "No incidents match the selected filters. Try adjusting your search criteria."
                        : "There are no incidents to display at the moment."}
                    </p>
                    {(selectedParentDomain || selectedDomain || selectedSubDomain || selectedProduct || searchValue || selectedCategory) && (
                      <button
                        onClick={resetAll}
                        className="mt-4 px-4 py-2 text-sm font-medium text-white bg-[#bb1ccc] rounded-lg hover:bg-blue-800"
                      >
                        Clear All Filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              displayIncidents.map((incident, index) => (
                <tr 
                  key={index} 
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span 
                      className="text-blue-600 font-medium hover:underline cursor-pointer"
                      onClick={() => {
                        if (incident.recordLink) {
                          window.open(incident.recordLink, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      title={incident.recordLink ? "Click to view incident details" : "No record link available"}
                    >
                      {incident.snowId}
                    </span>
                  </td>
                  <td
                    className="px-6 py-4 text-sm text-gray-900"
                    style={{ width: "300px", minWidth: "300px", maxWidth: "300px" }}
                  >
                    <Tooltip
                      arrow
                      placement="top-start"
                      disableHoverListener={!incident.incidentDescription}
                      enterDelay={300}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: "#616161",
                            color: "#ffffff",
                            boxShadow: "0px 6px 16px rgba(15,23,42,0.15)",
                            borderRadius: "8px",
                            maxWidth: 420,
                            maxHeight: 320,
                            overflowY: "auto",
                            p: 1.5,
                            fontSize: "12px",
                            "& .markdown-tooltip *": {
                              color: "#ffffff",
                              fontFamily: "Poppins, sans-serif",
                              margin: 0,
                              fontWeight: 400,
                            },
                            "& .markdown-tooltip strong": {
                              fontWeight: 600,
                            },
                          },
                        },
                        arrow: {
                          sx: { color: "#616161" },
                        },
                      }}
                      title={
                        incident.incidentDescription ? (
                          <Box className="markdown-tooltip">
                            <ReactMarkdown>{incident.incidentDescription}</ReactMarkdown>
                          </Box>
                        ) : (
                          ""
                        )
                      }
                    >
                      <div
                        className="break-words overflow-hidden prose prose-sm max-w-none"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        <ReactMarkdown>{incident.incidentDescription || ""}</ReactMarkdown>
                      </div>
                    </Tooltip>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {incident.displayIncidentCategory || incident.incidentCategory}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {incident.assignedTo}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium border-none rounded-sm ${
                      incident.status === 'Complete' ? 'bg-green-100 text-green-800' :
                      incident.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                      incident.status === 'New' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {incident.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`${
                      incident.resolutionPendingStatus === '0 days' ? 'text-green-600' :
                      parseInt(incident.resolutionPendingStatus) <= 2 ? 'text-yellow-600' :
                      'text-red-600'
                    } font-medium`}>
                      {incident.resolutionPendingStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {incident.createdAt ? dayjs(incident.createdAt).format("MMM D, YYYY") : "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

   
    </div>
  );
};

export default IncidentTable;


